/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { Fragment, type KeyboardEvent, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { Button } from './Button';
import { Icon } from './Icon';
import { Skeleton } from './States';

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T, index: number) => ReactNode;
  /** Right-aligned, tabular figures: every count, amount and share. */
  numeric?: boolean;
  align?: 'start' | 'center' | 'end';
  /** A CSS width for the column, such as '200px' or '30%'. */
  width?: string;
  /** Ids, tool and model names. */
  mono?: boolean;
  /** Left out at phone width when the table has no phone list. */
  hideOnPhone?: boolean;
  /** Text tones for secondary columns. */
  muted?: boolean;
  /** A class on the column's body cells (a screen's own width or layout rule in kit.css). */
  className?: string;
  /** The header is a button that sorts by this column (the table's `sort` says which column is sorted now). */
  sortable?: boolean;
}

export type SortDirection = 'asc' | 'desc';

/** Which column the rows are sorted by, and which way; the caller sorts (or asks the service to) and re-renders. */
export interface TableSort {
  key: string;
  direction: SortDirection;
  /** A click on a sortable header: the same key flips the direction, another key starts with its own default. */
  onSort: (key: string) => void;
}

/** Cursor paging: a "Load more" under the rows while the service has a next page. */
export interface TableMore {
  /** The next page's cursor, or null when every row is shown (the control disappears). */
  cursor: string | null;
  loading?: boolean;
  onMore: (cursor: string) => void;
  label?: string;
}

/** How one row reads as a list item at phone width (tables turn into lists below 820 px). */
export interface PhoneRow<T> {
  title: (row: T) => ReactNode;
  meta?: (row: T) => ReactNode;
  value?: (row: T) => ReactNode;
  sub?: (row: T) => ReactNode;
}

export interface TableProps<T> {
  /** The table's accessible name. */
  label: string;
  columns: Column<T>[];
  rows: readonly T[];
  rowKey: (row: T, index: number) => string;
  /** An element id for a row, so a deep link can scroll to it. */
  rowId?: (row: T, index: number) => string | undefined;
  /** A total row under the rows, by column key. */
  total?: Partial<Record<string, ReactNode>>;
  /** The phone list's total line. */
  phoneTotal?: { title: ReactNode; meta?: ReactNode; value?: ReactNode };
  /** Rows sharing a group value get a group row above them, in the order given. */
  groupOf?: (row: T) => string;
  /** The selected row: an accent bar, a tint and aria-current="true" (a plain table row has no aria-selected). */
  selectedKey?: string | null;
  /** Makes rows clickable (and reachable by keyboard). */
  onRowClick?: (row: T) => void;
  /** A row's open detail (a trace's tools, an approval's review), drawn in a full-width row under it when not null. */
  detail?: (row: T, index: number) => ReactNode;
  /** Skeleton rows in the final layout while the data loads. */
  loading?: boolean;
  skeletonRows?: number;
  /** What shows instead of an empty table (an EmptyState). */
  empty?: ReactNode;
  phone?: PhoneRow<T>;
  /**
   * Below 820 px, each row stacks its cells as label-and-value lines (the label is the column's text header), so a wide
   * or editable table that cannot become a phone list still fits the screen. Clicks, details and group rows keep working.
   */
  stacked?: boolean;
  /** Keep the header row in view while the page scrolls (default). */
  stickyHeader?: boolean;
  /** Sortable columns: the header of a `sortable` column becomes a button, and the sorted one shows its caret. */
  sort?: TableSort;
  /** Cursor paging under the rows. */
  more?: TableMore;
  className?: string;
}

/** The text a stacked cell is labelled with at phone width: the column header when it is plain text. */
function headerText<T>(column: Column<T>): string | undefined {
  return typeof column.header === 'string' ? column.header : undefined;
}

function cellClass<T>(column: Column<T>, header = false): string | undefined {
  const align = column.align ?? (column.numeric ? 'end' : 'start');
  const classes = [
    align === 'end' ? 'is-end' : align === 'center' ? 'is-center' : '',
    column.numeric ? 'is-num' : '',
    column.mono && !header ? 'is-mono' : '',
    column.muted && !header ? 'is-muted' : '',
    column.hideOnPhone ? 'hide-phone' : '',
    column.className && !header ? column.className : '',
  ].filter(Boolean);
  return classes.length ? classes.join(' ') : undefined;
}

/**
 * The one table: a 36 px header (sticky under the page header), 44 px rows, numbers right-aligned in tabular figures,
 * hover and selected states, optional group and total rows, skeleton rows while loading, and an EmptyState when there
 * is nothing. With `phone`, it is a list below 820 px instead.
 */
export function Table<T>({
  label, columns, rows, rowKey, rowId, total, phoneTotal, groupOf, selectedKey, onRowClick, detail, loading = false, skeletonRows = 4,
  empty, phone, stacked = false, stickyHeader = true, sort, more, className,
}: TableProps<T>) {
  if (!loading && rows.length === 0 && empty) return <>{empty}</>;

  const interactive = Boolean(onRowClick);
  const onKey = (event: KeyboardEvent<HTMLTableRowElement>, row: T) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick?.(row);
    }
  };

  // A group row goes above the first row of each run of rows that share a group value.
  const startsGroup = rows.map((row, index) => {
    if (!groupOf) return undefined;
    const group = groupOf(row);
    return index === 0 || groupOf(rows[index - 1] as T) !== group ? group : undefined;
  });
  const body = loading
    ? Array.from({ length: skeletonRows }, (_, index) => (
      <tr key={`sk-${index}`} className="is-skeleton" aria-hidden="true">
        {columns.map((column, c) => (
          <td key={column.key} className={cellClass(column)} data-th={headerText(column)}>
            <Skeleton width={column.numeric ? '48px' : c === 0 ? '96px' : '64px'} />
          </td>
        ))}
      </tr>
    ))
    : rows.map((row, index) => {
      const key = rowKey(row, index);
      const group = startsGroup[index];
      const groupRow = group !== undefined
        ? <tr className="is-group"><th scope="rowgroup" colSpan={columns.length}>{group}</th></tr>
        : null;
      const selected = selectedKey !== undefined && selectedKey !== null && key === selectedKey;
      const open = detail ? detail(row, index) : null;
      return (
        <Fragment key={key}>
          {groupRow}
          <tr
            id={rowId?.(row, index)}
            className={[selected ? 'is-selected' : '', interactive ? 'is-interactive' : ''].filter(Boolean).join(' ') || undefined}
            aria-current={interactive && selected ? 'true' : undefined}
            tabIndex={interactive ? 0 : undefined}
            onClick={interactive ? () => onRowClick?.(row) : undefined}
            onKeyDown={interactive ? (event) => onKey(event, row) : undefined}
          >
            {columns.map((column) => (
              <td key={column.key} className={cellClass(column)} data-th={headerText(column)}>{column.cell(row, index)}</td>
            ))}
          </tr>
          {open !== null && open !== undefined && open !== false ? (
            <tr className="is-detail"><td colSpan={columns.length}>{open}</td></tr>
          ) : null}
        </Fragment>
      );
    });

  const table = (
    <table className={['ui-table', stickyHeader ? 'is-sticky' : '', phone ? 'has-phone-list' : '', stacked ? 'is-stacked' : '', className ?? ''].filter(Boolean).join(' ')} aria-label={label} aria-busy={loading || undefined}>
      <thead>
        <tr>
          {columns.map((column) => (
            // A column's width sits on its header cell, so a column hidden at phone width takes its width with it.
            // Computed widths are the one inline style the kit allows (tokens cannot know a column's share).
            <th
              key={column.key}
              scope="col"
              className={cellClass(column, true)}
              aria-sort={sort && column.sortable && sort.key === column.key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
              style={column.width ? { width: column.width } : undefined}
            >
              {sort && column.sortable ? <SortHeader column={column} sort={sort} /> : column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {body}
        {total && !loading && rows.length > 0 ? (
          <tr className="is-total">
            {columns.map((column, index) => (
              index === 0
                ? <th key={column.key} scope="row" className={cellClass(column)}>{total[column.key] ?? 'Total'}</th>
                : <td key={column.key} className={cellClass(column)} data-th={headerText(column)}>{total[column.key] ?? null}</td>
            ))}
          </tr>
        ) : null}
      </tbody>
    </table>
  );

  // "Load more" while the service has a next page; nothing once every row is shown.
  const pager = more && more.cursor && !loading ? (
    <div className="ui-table-more">
      <Button size="sm" busy={more.loading} onClick={() => more.onMore(more.cursor as string)}>{more.label ?? 'Load more'}</Button>
    </div>
  ) : null;

  if (!phone) return pager ? <>{table}{pager}</> : table;
  return (
    <>
      {table}
      <ul className="ui-mlist" aria-label={label}>
        {loading
          ? Array.from({ length: skeletonRows }, (_, index) => (
            <li key={`sk-${index}`} className="ui-mrow" aria-hidden="true">
              <div className="ui-mrow-main"><Skeleton width="96px" /><Skeleton width="140px" /></div>
              <div className="ui-mrow-side"><Skeleton width="56px" /></div>
            </li>
          ))
          : rows.map((row, index) => (
            <li key={rowKey(row, index)} className="ui-mrow">
              <div className="ui-mrow-main">
                <span className="ui-mrow-title">{phone.title(row)}</span>
                {phone.meta ? <span className="ui-mrow-meta">{phone.meta(row)}</span> : null}
              </div>
              {phone.value || phone.sub ? (
                <div className="ui-mrow-side">
                  {phone.value ? <span className="ui-mrow-value">{phone.value(row)}</span> : null}
                  {phone.sub ? <span className="ui-mrow-meta">{phone.sub(row)}</span> : null}
                </div>
              ) : null}
            </li>
          ))}
        {phoneTotal && !loading && rows.length > 0 ? (
          <li className="ui-mrow is-total">
            <div className="ui-mrow-main">
              <span className="ui-mrow-title">{phoneTotal.title}</span>
              {phoneTotal.meta ? <span className="ui-mrow-meta">{phoneTotal.meta}</span> : null}
            </div>
            {phoneTotal.value ? <div className="ui-mrow-side"><span className="ui-mrow-value">{phoneTotal.value}</span></div> : null}
          </li>
        ) : null}
      </ul>
      {pager}
    </>
  );
}

/** A sortable column's header: the text and a caret, the sorted column's in the accent (the mockup's "Asks ▾"). */
function SortHeader<T>({ column, sort }: { column: Column<T>; sort: TableSort }) {
  const active = sort.key === column.key;
  const caret = !active ? ChevronsUpDown : sort.direction === 'asc' ? ChevronUp : ChevronDown;
  const name = headerText(column);
  return (
    <button
      type="button"
      className={`ui-th-sort${active ? ' is-active' : ''}`}
      onClick={() => sort.onSort(column.key)}
      title={active ? `Sorted by ${name ?? 'this column'}, ${sort.direction === 'asc' ? 'ascending' : 'descending'}; click to flip` : `Sort by ${name ?? 'this column'}`}
    >
      {column.header}
      <Icon icon={caret} size="xs" />
    </button>
  );
}
